import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LogOut, ShieldCheck, BookOpen, Trophy, User } from 'lucide-react';
import { Button } from '@nzassa/ui';
import { COLORS, ADMIN_ROLES } from '@nzassa/shared';
import { useAuthStore } from '@/stores/authStore';
import { NotificationBell } from './NotificationBell';

export function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuthStore();

  const isAdmin = user && ADMIN_ROLES.includes(user.role);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 w-full" aria-label={t('nav.navigation_principale')}>
      {/* Brand Bar */}
      <div className="h-1 w-full" style={{ background: COLORS.orange }} />

      {/* Nav Content */}
      <div className="border-b border-slate-200/60 bg-white/80 backdrop-blur-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
            aria-label="NzassaLearn — Accueil"
          >
            <div
              className="grid h-9 w-9 place-items-center rounded-xl shadow-sm transition-transform group-hover:scale-105"
              style={{ background: COLORS.blue }}
            >
              <img src="/logos/nzassalearn-compact.svg" alt="" className="h-5 w-5" />
            </div>
            <div className="leading-tight text-left">
              <div
                className="text-sm font-bold tracking-tight"
                style={{ color: COLORS.blue }}
              >
                NzassaLearn
              </div>
              <div className="text-[11px] font-medium text-slate-500">
                {t('nav.brand_subtitle')}
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <>
                {/* Admin navigation */}
                {isAdmin && (
                  <Button
                    variant={location.pathname.startsWith('/admin') ? 'secondary' : 'ghost'}
                    className={`hidden sm:inline-flex rounded-xl ${
                      location.pathname.startsWith('/admin') ? '' : 'text-slate-600'
                    }`}
                    size="sm"
                    onClick={() => navigate('/admin')}
                    aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    {t('nav.admin')}
                  </Button>
                )}

                <Button
                  variant={location.pathname === '/dashboard' ? 'secondary' : 'ghost'}
                  className={`hidden sm:inline-flex rounded-xl ${
                    location.pathname === '/dashboard' ? '' : 'text-slate-600'
                  }`}
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  {t('nav.apprendre')}
                </Button>

                <Button
                  variant={location.pathname === '/leaderboard' ? 'secondary' : 'ghost'}
                  className={`hidden sm:inline-flex rounded-xl ${
                    location.pathname === '/leaderboard' ? '' : 'text-slate-600'
                  }`}
                  size="sm"
                  onClick={() => navigate('/leaderboard')}
                  aria-current={location.pathname === '/leaderboard' ? 'page' : undefined}
                >
                  <Trophy className="mr-2 h-4 w-4" />
                  {t('nav.classement')}
                </Button>

                <div className="h-6 w-px bg-slate-200 mx-1 hidden sm:block" />

                <NotificationBell />

                <Button
                  variant={location.pathname === '/profile' ? 'secondary' : 'ghost'}
                  className={`hidden sm:inline-flex rounded-xl ${
                    location.pathname === '/profile' ? '' : 'text-slate-600'
                  }`}
                  size="sm"
                  onClick={() => navigate('/profile')}
                  aria-current={location.pathname === '/profile' ? 'page' : undefined}
                >
                  <User className="mr-2 h-4 w-4" />
                  {t('nav.profil')}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-xl text-slate-500 hover:text-red-500"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            )}
            {!isAuthenticated && (
              <>
                {!['/connexion', '/admin-login', '/auth', '/a-propos'].includes(location.pathname) && (
                  <>
                    <Button
                      variant={location.pathname === '/a-propos' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="hidden sm:inline-flex rounded-xl text-slate-600"
                      onClick={() => navigate('/a-propos')}
                    >
                      {t('nav.a_propos')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => navigate('/connexion')}
                    >
                      {t('nav.connexion')}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
